<template>
    <div
        @click="togglePopover"
        @keydown.enter="handleEnterKey"
        @keydown.space.prevent="handleEnterKey"
         @keydown.escape="handleEscapeKey"
         :key="person?.id"
        class="header-acc"
        :class="[$attrs.class, { 'active-popover': popover?.visible }]"
        :tabindex="0"
        role="button"
        :aria-expanded="popover?.visible"
        aria-haspopup="true"
        ref="triggerElement"
    >
        <PersonAvatar :name="person?.name" :person_id="person?.id || 0" :width="40" :height="40" class="flex-shrink-0" />

        <div class="acc-wrapper">
            <div class="name-div">
                <span
                    ref="nameSpan"
                    v-tooltip.top="isNameTruncated ? null : null"
                    @mouseenter="show" @mouseleave="hide"
                >{{ person?.name }}</span>
                <!-- simulation of upgrade license -->
                <p v-if="person?.name">{{ person.license ? person.license.type : 'Unlicensed' }}</p>
                <p v-else>Loading...</p>
            </div>
            <div class="icon-drop">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                        d="M5.83325 12.5L9.99992 16.6666L14.1666 12.5M5.83325 7.49998L9.99992 3.33331L14.1666 7.49998"
                        stroke="#A4A7AE"
                        stroke-width="1.66667"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </div>
        </div>
        <PrimePopover
            ref="popoverName"
            class="name-popover"
            :pt="{
                content: {
                    class: 'popover-content'
                },
            }"
        >
            <div class="status-tooltip-content">
                <div class="status-item">
                    <p class="status-description">{{ person?.name }}</p>
                </div>
                
            </div>
        </PrimePopover>
    </div>

    <PrimePopover ref="popover" class="w-[calc(100vw-2rem)] lg:w-auto" @hide="handlePopoverHide" @show="handlePopoverShow">
        <AccountPopover @hidePopover="hidePopover" ref="accountPopoverRef" />
    </PrimePopover>
</template>
<script setup lang="ts">
import PrimePopover from 'primevue/popover';
import PersonAvatar from '~/components/main/shared/PersonAvatar.vue';
import { usePersonStore } from '~/stores/person';
import AccountPopover from './AccountPopover.vue';
const store = usePersonStore();
const popover = ref();
const popoverName = ref();
const tooltipIcon = ref();
const triggerElement = ref();
const accountPopoverRef = ref();
const nameSpan = ref<HTMLElement>();

const { person } = storeToRefs(store);

const isNameTruncated = ref(false);

const checkNameTruncation = () => {
    if (!nameSpan.value) {
        isNameTruncated.value = false;
        return;
    }
    isNameTruncated.value = nameSpan.value.scrollWidth > nameSpan.value.clientWidth;
};

// Watch for person name changes and recheck truncation
watch(() => person.value?.name, () => {
    nextTick(() => {
        checkNameTruncation();
        // Hide popover if name is no longer truncated
        if (!isNameTruncated.value && popoverName.value?.visible) {
            popoverName.value.hide();
        }
    });
}, { immediate: true });

onMounted(() => {
    checkNameTruncation();
});

const togglePopover = (event: any) => {
    popover.value.toggle(event);
};

const handleEnterKey = (event: KeyboardEvent) => {
    event.preventDefault();
    togglePopover(event);
};

const handleEscapeKey = (event: KeyboardEvent) => {
    if (popover.value?.visible) {
        event.preventDefault();
        hidePopover();
        // Return focus to trigger element
        nextTick(() => {
            triggerElement.value?.focus();
        });
    }
};

const handlePopoverShow = () => {
};

const handlePopoverHide = () => {
    // Return focus to trigger when popover closes
    nextTick(() => {
        triggerElement.value?.focus();
    });
};

const hidePopover = () => {
    popover.value.hide();
};

const show = (event: Event) => {
    if (isNameTruncated.value) {
        popoverName.value.show(event, nameSpan.value);
    }
};

const hide = (event: Event) => {
    if (isNameTruncated.value) {
        popoverName.value.hide();
    }
};
</script>
<style lang="scss" scoped>
.header-acc {
    @apply flex p-3 lg:w-[280px] cursor-pointer items-center gap-4 rounded-lg border lg:border-gray-200;
    @apply outline-none bg-zinc-900 lg:bg-white;

    &:hover, &:focus {
        transform: translateY(-1px);
        @apply shadow-md bg-zinc-800 lg:bg-white;
    }

    &.active-popover {
        @apply shadow-md bg-zinc-800 lg:bg-white;
    }

    .icon-div {
        @apply flex justify-center items-center w-10 h-10 p-2 aspect-square rounded-full border border-gray-200 bg-gray-100;
    }

    .acc-wrapper {
        @apply flex justify-between items-center w-full;

        .name-div {
            @apply flex flex-col items-start;

            span {
                @apply text-white lg:text-gray-900 text-sm font-semibold leading-5 font-inter truncate max-w-full lg:max-w-[165px];
            }
            p {
                @apply text-gray-400 lg:text-gray-600 text-sm font-normal leading-5 font-inter overflow-hidden text-ellipsis line-clamp-1;
            }
        }

        .icon-drop {
            @apply flex w-8 h-8 p-1.5 justify-center items-center content-center flex-wrap;
        }
    }
}

.status-tooltip-content {
    @apply flex flex-col items-start rounded-lg bg-zinc-900 p-3 gap-2;
    min-width: 200px;
}

.status-item {
    @apply flex flex-col;
}

.status-description {
    @apply text-zinc-200 font-inter text-xs font-medium leading-5;
}

:global(.name-popover[data-pc-section="root"]) {
    border-radius: 8px !important;
}


// Hide tooltip preset arrows
::deep(.p-tooltip--dark.p-tooltip--top)::after {
    display: none !important;
}

.name-popover::after,
.name-popover[data-pc-section="root"]::after,
.name-popover :deep([data-pc-section="arrow"]),
.name-popover :deep(.p-popover-arrow) {
    display: none !important;
}

:global(.name-popover[data-pc-section="root"]::before) {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    left: 50%;
    top: -8px;
    transform: translateX(-50%);
    border-style: solid;
    border-width: 0 8px 8px 8px;
    border-color: transparent transparent #1f1f23 transparent;
}
[role="tooltip"].p-tooltip--dark.p-tooltip--top::after,
.p-tooltip--dark.p-tooltip--top::after,
[role=tooltip].p-tooltip--dark.p-tooltip--top::after, .p-tooltip--dark.p-tooltip--top::after 
 {
  display: none !important ;
}
</style>
